import { PayloadAction } from "@reduxjs/toolkit";
import { put, takeLatest, call, all, select } from "redux-saga/effects";

import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { commonMessages } from "infrastructure/localization";
import { notification } from "infrastructure/notification";
import { IValidationIssue, ValidationIssueSeverity } from "infrastructure/validation";
import { IPickerSearchCriterion, applyPickerFilter } from "ui/shared/helpers/plex-components";
import { extractApiErrorMessage, extractApiErrorMessageContent } from "ui/shared/models/common";

import { releaseMessages } from "../release.localization";

import { releaseDetailsManagerActions } from "./ReleaseDetails.actions";
import {
  getReleaseDetails,
  getReleaseNameMessage,
  freezeRelease,
  shipRelease,
  getAllTerms,
  getAssociatedTerms,
  getMessages,
  getAssociatedMessages,
  unfreezeRelease,
  updateRelease
} from "./ReleaseDetails.api";
import { ITerm, IMessage, IReleaseDetailsResponseError, IReleaseDetailsState, IReleaseChangeData, Status, ITermData, IMessageData } from "./ReleaseDetails.state";

function* loadReleaseDataExecute({ payload }: PayloadAction<string>) {
  try {
    const [releaseDetails, terms, messages] = yield all([call(getReleaseDetails, payload), call(getAssociatedTerms, payload), call(getAssociatedMessages, payload)]);
    const releaseNameMessage = yield call(getReleaseNameMessage, releaseDetails.name);
    releaseDetails.name = releaseNameMessage.text;
    yield put(releaseDetailsManagerActions.loadReleaseDataCompleted({ releaseDetails: releaseDetails, messages: messages, terms: terms }));
  } catch (error) {
    yield put(releaseDetailsManagerActions.loadReleaseDataFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* freezeReleaseExecute({ payload }: PayloadAction<string>) {
  try {
    yield call(freezeRelease, payload);
    yield put(releaseDetailsManagerActions.updateStatusCompleted(notification.success().useMessageId(releaseMessages.info("releaseFreezeCompleted"))));
  } catch (error) {
    put(releaseDetailsManagerActions.updateStatusFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* unfreezeReleaseExecute({ payload }: PayloadAction<string>) {
  try {
    yield call(unfreezeRelease, payload);
    yield put(releaseDetailsManagerActions.updateStatusCompleted(notification.success().useMessageId(releaseMessages.info("releaseUnfreezeCompleted"))));
  } catch (error) {
    put(releaseDetailsManagerActions.updateStatusFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* shipReleaseExecute({ payload }: PayloadAction<string>) {
  try {
    yield call(shipRelease, payload);
    yield put(releaseDetailsManagerActions.updateStatusCompleted(notification.success().useMessageId(releaseMessages.info("releaseShipCompleted"))));
  } catch (error) {
    put(releaseDetailsManagerActions.updateStatusFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* loadActualTermsExecute({ payload }: PayloadAction<IPickerSearchCriterion<ITerm>>) {
  try {
    let terms: ITerm[] = yield call(getAllTerms, payload.query.query);
    terms = applyPickerFilter(terms, payload, row => row.term);
    yield put(releaseDetailsManagerActions.loadPickerTermsCompleted(terms));
  } catch (error) {
    yield put(
      releaseDetailsManagerActions.loadPickerTermsFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error(error).useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error(error).useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* loadMessagesWithoutRelationshipsExecute({ payload }: PayloadAction<IPickerSearchCriterion<IMessage>>) {
  try {
    let messages: IMessage[] = yield call(getMessages, payload.query.query);
    messages = applyPickerFilter(messages, payload, row => row.messageId);
    yield put(releaseDetailsManagerActions.loadPickerMessagesCompleted(messages));
  } catch (error) {
    yield put(releaseDetailsManagerActions.loadPickerMessagesFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* getErrorRecords(error: HttpError) {
  const errorModels: IReleaseDetailsResponseError[] = error.data;
  const state: IReleaseDetailsState = yield select((state: { releaseDetails: IReleaseDetailsState }) => state.releaseDetails);

  const termUpdatedRecords: ITermData[] = [];
  const messageUpdatedRecords: IMessageData[] = [];

  for (const row of state.associatedTerms) {
    const issues: IValidationIssue[] = [];
    for (const model of errorModels) {
      if (row.term.termId === model.entityId) {
        issues.push({
          message: extractApiErrorMessageContent(model.error),
          severity: ValidationIssueSeverity.Error
        });
      }
    }
    const data: ITermData = { term: row.term, status: row.status, validationIssues: issues };
    termUpdatedRecords.push(data);
  }

  for (const row of state.associatedMessages) {
    const issues: IValidationIssue[] = [];
    for (const model of errorModels) {
      if (row.message.messageId === model.entityId) {
        issues.push({
          message: extractApiErrorMessageContent(model.error),
          severity: ValidationIssueSeverity.Error
        });
      }
    }
    const data: IMessageData = { message: row.message, status: row.status, validationIssues: issues };
    messageUpdatedRecords.push(data);
  }

  return [termUpdatedRecords, messageUpdatedRecords];
}

function* updateReleaseExecute({ payload }: PayloadAction<string>) {
  try {
    const releaseDetailsState: IReleaseDetailsState = yield select((state: { releaseDetails: IReleaseDetailsState }) => state.releaseDetails);
    const deletedMessages: IReleaseChangeData[] = releaseDetailsState.associatedMessages
      .filter(x => x.status === Status.Deleted)
      .map(m => ({ id: m.message.messageId, version: m.message.version }));
    const addedMessages: IReleaseChangeData[] = releaseDetailsState.associatedMessages
      .filter(x => x.status === Status.Added)
      .map(m => ({ id: m.message.messageId, version: m.message.version }));
    const deletedTerms: IReleaseChangeData[] = releaseDetailsState.associatedTerms
      .filter(x => x.status === Status.Deleted)
      .map(m => ({ id: m.term.termId, version: m.term.version }));
    const addedTerms: IReleaseChangeData[] = releaseDetailsState.associatedTerms.filter(x => x.status === Status.Added).map(m => ({ id: m.term.termId, version: m.term.version }));
    yield call(updateRelease, payload, { addedMessages: addedMessages, deletedMessages: deletedMessages, addedTerms: addedTerms, deletedTerms: deletedTerms });
    yield put(releaseDetailsManagerActions.updateReleaseCompleted(notification.warning().useMessageId(releaseMessages.info("releaseSuccessfulUpdated"))));
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const [termData, messageData]: [ITermData[], IMessageData[]] = yield call(getErrorRecords, error);
      yield put(
        releaseDetailsManagerActions.updateReleaseFailed({
          notifications: [notification.error().useMessageId(releaseMessages.error("releaseUpdateFailed"))],
          terms: termData,
          messages: messageData
        })
      );
    } else {
      yield put(
        releaseDetailsManagerActions.updateReleaseFailed({
          notifications: [notification.error().useMessageId(commonMessages.error("unexpectedError"))]
        })
      );
    }
  }
}

function* actionWatcher() {
  yield takeLatest(releaseDetailsManagerActions.loadReleaseData.toString(), loadReleaseDataExecute);
  yield takeLatest(releaseDetailsManagerActions.freezeRelease.toString(), freezeReleaseExecute);
  yield takeLatest(releaseDetailsManagerActions.unfreezeRelease.toString(), unfreezeReleaseExecute);
  yield takeLatest(releaseDetailsManagerActions.shipRelease.toString(), shipReleaseExecute);
  yield takeLatest(releaseDetailsManagerActions.loadPickerTerms.toString(), loadActualTermsExecute);
  yield takeLatest(releaseDetailsManagerActions.loadPickerMessages.toString(), loadMessagesWithoutRelationshipsExecute);
  yield takeLatest(releaseDetailsManagerActions.updateRelease.toString(), updateReleaseExecute);
}

export function* releaseDetailsSaga() {
  yield all([actionWatcher()]);
}
